/* Small, dependency-free storefront tools. No customer details leave the browser. */
(function () {
  'use strict';
  const KEY = 'tinloo.wishlist.v1';
  const LIMIT = 60;
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,199}$/.test(value);
  function readWishlist(storage) {
    try {
      const value = JSON.parse(storage.getItem(KEY) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter(validHandle))].slice(0, LIMIT) : [];
    } catch (_) { return []; }
  }
  function toggleWishlist(storage, handle) {
    if (!validHandle(handle)) throw new Error('This item cannot be saved. Please open its product page.');
    const items = readWishlist(storage);
    const saved = !items.includes(handle);
    if (saved && items.length >= LIMIT) throw new Error('Your wishlist has 60 items. Remove one to save another.');
    const next = saved ? [...items, handle] : items.filter(item => item !== handle);
    try { storage.setItem(KEY, JSON.stringify(next)); }
    catch (_) { throw new Error('Your browser cannot save favourites right now. Allow site storage and try again.'); }
    return { items: next, saved };
  }
  function checkPincode(value, coverage, demo) {
    const pin = String(value).trim();
    if (!/^[1-9][0-9]{5}$/.test(pin)) return { state: 'invalid', message: 'Enter a valid 6-digit Indian pincode.' };
    const list = new Set(String(coverage || '').split(/[\s,;]+/).filter(item => /^[1-9][0-9]{5}$/.test(item)));
    if (demo) return { state: 'demo', message: list.has(pin) ? 'Demo result: this pincode is in the sample list. Actual delivery availability is not confirmed.' : 'Demo result: this pincode is outside the sample list. This does not mean delivery is unavailable.' };
    if (!list.size) return { state: 'unknown', message: 'Delivery coverage is being updated. Please contact us to confirm your pincode.' };
    return list.has(pin) ? { state: 'covered', message: 'Your pincode is in our delivery area. Final delivery options and charges appear at checkout.' } : { state: 'unknown', message: 'Your pincode is not in our current list. Please contact us to confirm delivery.' };
  }
  function whatsappUrl(phone, message) {
    const raw = String(phone || '').trim();
    if (!/^\+?[1-9][0-9\s()-]{7,20}$/.test(raw)) return null;
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 8 || digits.length > 15) return null;
    return 'https://wa.me/' + digits + '?text=' + encodeURIComponent(message);
  }
  const core = { KEY, LIMIT, readWishlist, toggleWishlist, checkPincode, whatsappUrl };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined') return;

  const settingsElement = document.getElementById('tinloo-shopping-settings');
  if (!settingsElement) return;
  let config;
  try { config = JSON.parse(settingsElement.textContent); } catch (_) { return; }
  const root = config.root || '/';
  const pageUrl = path => root + path;
  const announce = message => {
    const status = document.getElementById('tinloo-shopping-status');
    if (status) status.textContent = message;
  };
  const getItems = () => {
    try { return readWishlist(window.localStorage); } catch (_) { return []; }
  };
  function syncButtons() {
    const items = getItems();
    document.querySelectorAll('[data-wishlist-handle]').forEach(button => {
      const saved = items.includes(button.dataset.wishlistHandle);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', (saved ? 'Remove from wishlist: ' : 'Save to wishlist: ') + (button.dataset.wishlistTitle || 'this item'));
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Save to wishlist';
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(node => { node.textContent = items.length ? String(items.length) : ''; });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-wishlist-handle]');
    if (!button) return;
    event.preventDefault();
    try {
      const result = toggleWishlist(window.localStorage, button.dataset.wishlistHandle);
      announce(result.saved ? 'Added to your wishlist.' : 'Removed from your wishlist.');
      syncButtons();
      document.dispatchEvent(new CustomEvent('tinloo:wishlist-changed'));
    } catch (error) { announce(error.message); }
  });

  class TinlooPincode extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.querySelector('form').addEventListener('submit', event => {
        event.preventDefault();
        const result = checkPincode(this.querySelector('input').value, config.pincodes, config.pincodeDemo);
        const input = this.querySelector('input');
        input.setAttribute('aria-invalid', String(result.state === 'invalid'));
        const status = this.querySelector('[data-pincode-result]');
        status.textContent = result.message;
        status.dataset.state = result.state;
      });
    }
  }
  if (!customElements.get('tinloo-pincode')) customElements.define('tinloo-pincode', TinlooPincode);

  class TinlooWishlist extends HTMLElement {
    connectedCallback() {
      this.version = 0;
      this.refresh = () => this.render();
      document.addEventListener('tinloo:wishlist-changed', this.refresh);
      this.render();
    }
    disconnectedCallback() { document.removeEventListener('tinloo:wishlist-changed', this.refresh); this.version++; }
    async render() {
      const version = ++this.version;
      const list = this.querySelector('[data-wishlist-list]');
      const empty = this.querySelector('[data-wishlist-empty]');
      const status = this.querySelector('[data-wishlist-status]');
      const items = getItems();
      list.replaceChildren();
      empty.hidden = items.length > 0;
      status.textContent = items.length ? 'Loading your favourites…' : '';
      if (!items.length) return;
      const results = await Promise.all(items.map(async handle => {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);
          let response;
          try { response = await fetch(pageUrl('products/' + encodeURIComponent(handle) + '.js'), { signal: controller.signal, credentials: 'same-origin' }); }
          finally { clearTimeout(timeout); }
          if (!response.ok) return { handle, unavailable: true };
          const product = await response.json();
          return { handle, product };
        } catch (_) { return { handle, failed: true }; }
      }));
      if (version !== this.version || !this.isConnected) return;
      let failures = 0;
      results.forEach(result => {
        const card = document.createElement('li');
        card.className = 'tinloo-wishlist-card';
        const link = document.createElement('a');
        link.href = pageUrl('products/' + result.handle);
        if (result.product) {
          const product = result.product;
          if (product.featured_image) {
            const source = new URL(product.featured_image, window.location.origin);
            if (source.protocol === 'https:' || source.origin === window.location.origin) {
              const image = document.createElement('img');
              image.src = source.href;
              image.alt = product.title;
              image.width = 480;
              image.height = 600;
              image.loading = 'lazy';
              link.append(image);
            }
          }
          const heading = document.createElement('h2');
          heading.textContent = product.title;
          link.append(heading);
          const price = document.createElement('p');
          try { price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: config.currency }).format(product.price / 100); }
          catch (_) { price.textContent = ''; }
          link.append(price);
          if (!product.available) {
            const sold = document.createElement('p'); sold.textContent = 'Currently unavailable'; link.append(sold);
          }
          card.append(link);
        } else {
          failures++;
          const heading = document.createElement('h2');
          heading.textContent = 'A saved favourite';
          const message = document.createElement('p');
          message.textContent = result.failed ? 'Could not load this item. Check your connection and try again.' : 'This item is no longer available to view.';
          card.append(heading, message);
        }
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'tinloo-text-link';
        remove.dataset.wishlistHandle = result.handle;
        remove.dataset.wishlistTitle = result.product ? result.product.title : 'saved item';
        remove.textContent = 'Remove';
        card.append(remove); list.append(card);
      });
      status.textContent = failures ? 'Some saved items could not be loaded. Your wishlist has been kept.' : items.length + (items.length === 1 ? ' saved favourite.' : ' saved favourites.');
      if (failures) {
        const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'tinloo-text-link'; retry.textContent = 'Try again';
        retry.addEventListener('click', () => this.render(), { once: true }); status.append(' ', retry);
      }
      syncButtons();
    }
  }
  if (!customElements.get('tinloo-wishlist')) customElements.define('tinloo-wishlist', TinlooWishlist);

  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-tinloo-chat]');
    if (!trigger) return;
    const dialog = document.getElementById('tinloo-chat-dialog');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    event.preventDefault();
    const productTitle = trigger.dataset.productTitle || config.productTitle;
    const productUrl = trigger.dataset.productUrl || config.productUrl;
    const message = productTitle ? 'Hi Tinloo, I would like to know more about ' + productTitle + '. ' + productUrl : 'Hi Tinloo, I have a question about your collection.';
    const link = dialog.querySelector('[data-whatsapp-link]');
    const url = whatsappUrl(config.whatsapp, message);
    link.hidden = !url;
    if (url) link.href = url; else link.removeAttribute('href');
    dialog.querySelector('[data-whatsapp-pending]').hidden = Boolean(url);
    dialog.querySelector('[data-chat-product]').textContent = productTitle ? 'Your enquiry: ' + productTitle : 'Questions about an outfit, sizing or your order? We’re here to help.';
    dialog.showModal();
  });
  const dialog = document.getElementById('tinloo-chat-dialog');
  if (dialog) dialog.querySelector('[data-chat-close]').addEventListener('click', () => dialog.close());
  window.addEventListener('storage', event => {
    if (event.key === KEY || event.key === null) { syncButtons(); document.dispatchEvent(new CustomEvent('tinloo:wishlist-changed')); }
  });
  document.addEventListener('shopify:section:load', syncButtons);
  const observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-wishlist-handle]') || node.querySelector('[data-wishlist-handle]'))))) syncButtons();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  syncButtons();
})();
